import type { Edge, Node } from '../../../core/models/scene';
import type { OrgAllocationAssignment } from './OrgAllocationService';

export type OrgAllocationGanttGranularity = 'week' | 'month' | 'quarter';
export type OrgAllocationGanttRowGrouping =
  | 'auto'
  | 'person'
  | 'manager'
  | 'team'
  | 'department'
  | 'target';
export type OrgAllocationGanttLoadBand =
  | 'empty'
  | 'under'
  | 'near_capacity'
  | 'at_capacity'
  | 'over'
  | 'severe_over'
  | 'unknown';

export type OrgAllocationGanttSourceState =
  | 'source_backed'
  | 'draft'
  | 'needs_review'
  | 'stale'
  | 'retired'
  | 'unresolved'
  | 'low_confidence';

export interface OrgAllocationGanttSceneOptions {
  readonly origin?: { readonly x: number; readonly y: number };
  readonly idPrefix?: string;
  readonly asOf?: Date | string | null;
  readonly granularity?: OrgAllocationGanttGranularity;
  readonly windowStart?: Date | string | null;
  readonly windowEnd?: Date | string | null;
  readonly rowGrouping?: OrgAllocationGanttRowGrouping;
  readonly aggregateThreshold?: number;
  readonly maxPeople?: number;
  readonly maxPeriods?: number;
}

export interface OrgAllocationGanttScene {
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly granularity: OrgAllocationGanttGranularity;
  readonly periodLabels: readonly string[];
  readonly cellSummaries: readonly OrgAllocationGanttCellSummary[];
  readonly scaleSummary: OrgAllocationGanttScaleSummary;
  readonly rowGrouping: OrgAllocationGanttRowGrouping;
  readonly aggregateField?: string;
  readonly hiddenPeopleCount: number;
  readonly hiddenPeriodCount: number;
  readonly hiddenAssignmentCount: number;
}

export interface OrgAllocationGanttScaleSummary {
  readonly totalPeople: number;
  readonly visiblePeople: number;
  readonly totalPeriods: number;
  readonly visiblePeriods: number;
  readonly totalAssignments: number;
  readonly hiddenPeopleCount: number;
  readonly hiddenPeriodCount: number;
  readonly hiddenAssignmentCount: number;
  readonly bounded: boolean;
}

export interface OrgAllocationGanttCellSummary {
  readonly cellKey: string;
  readonly row: number;
  readonly column: number;
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly periodKey: string;
  readonly periodLabel: string;
  readonly knownLoad: number;
  readonly capacityUnits: number;
  readonly memberCount: number;
  readonly unknownAllocationCount: number;
  readonly targetCount: number;
  readonly assignmentCount: number;
  readonly sourceCount: number;
  readonly loadBand: OrgAllocationGanttLoadBand;
  readonly overage: number;
  readonly rowKind: 'person' | 'aggregate';
  readonly aggregateField?: string;
  readonly aggregateValue?: string;
}

interface GanttPeriod {
  readonly key: string;
  readonly label: string;
  readonly start: Date;
  readonly endExclusive: Date;
}

interface PersonBucket {
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly rowKind: 'person' | 'aggregate';
  readonly aggregateField?: string;
  readonly aggregateValue?: string;
  readonly aggregateFilterField?: string;
  readonly aggregateFilterValue?: string;
  readonly memberCount: number;
  readonly capacityUnits: number;
  readonly assignments: readonly OrgAllocationAssignment[];
  readonly totalKnownAllocation: number;
  readonly assignmentCount: number;
}

interface BarGroup {
  readonly targetExternalId: string;
  readonly targetLabel: string;
  readonly targetKind: string;
  readonly role: string | null;
  readonly allocation: number;
  readonly hasUnknownAllocation: boolean;
  readonly sourceState: OrgAllocationGanttSourceState;
  readonly confidence: number | null;
  readonly sourceLabels: readonly string[];
  readonly assignments: readonly OrgAllocationAssignment[];
}

interface InternalCellSummary extends OrgAllocationGanttCellSummary {
  readonly groups: readonly BarGroup[];
}

type TableCellStyle = {
  readonly fillColor?: string;
  readonly textColor?: string;
  readonly fontWeight?: 'normal' | 'bold';
  readonly fontSize?: number;
  readonly textAlign?: 'left' | 'center' | 'right';
  readonly verticalAlign?: 'top' | 'middle' | 'bottom';
  readonly borderColor?: string;
  readonly borderWidth?: number;
  readonly borderDash?: readonly number[];
};

const DEFAULT_ORIGIN = { x: 96, y: 120 } as const;
const DEFAULT_MAX_PEOPLE = 12;
const DEFAULT_MAX_PERIODS = 12;
const DEFAULT_AGGREGATE_THRESHOLD = 12;
const PERSON_COLUMN_WIDTH = 220;
const PERIOD_COLUMN_WIDTH = 164;
const HEADER_HEIGHT = 50;
const ROW_HEIGHT = 96;
const BAR_X_PADDING = 10;
const BAR_BOTTOM_PADDING = 10;
const BAR_GAP = 4;
const MIN_BAR_HEIGHT = 16;
const UNKNOWN_BAR_HEIGHT = 20;
const CAPACITY_GUIDE_HEIGHT = 2;
const LEGEND_ITEM_WIDTH = 124;
const LEGEND_ITEM_HEIGHT = 24;
const LEGEND_GAP = 8;
const MAX_OPEN_ENDED_MONTHS = 3;
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const TARGET_PALETTE = [
  '#2563EB',
  '#7C3AED',
  '#059669',
  '#EA580C',
  '#DB2777',
  '#0891B2',
  '#CA8A04',
  '#DC2626',
  '#4F46E5',
  '#0D9488',
] as const;

const AGGREGATE_SPECS = [
  {
    id: 'manager',
    label: 'Manager',
    filterField: 'manager',
    sourceFields: ['Manager', 'Manager Name', 'Manager Email', 'Reports To', 'Supervisor'],
  },
  {
    id: 'team',
    label: 'Team',
    filterField: 'team',
    sourceFields: ['Team', 'Squad', 'Group', 'Pod'],
  },
  {
    id: 'department',
    label: 'Department',
    filterField: 'department',
    sourceFields: ['Department', 'Dept', 'Function', 'Org Unit', 'Business Unit'],
  },
  {
    id: 'target',
    label: 'Target',
    filterField: 'targetExternalId',
    sourceFields: [],
  },
] as const;

type AggregateSpec = (typeof AGGREGATE_SPECS)[number];

export function buildOrgAllocationGanttScene(
  assignments: readonly OrgAllocationAssignment[],
  options: OrgAllocationGanttSceneOptions = {},
): OrgAllocationGanttScene {
  const relevantAssignments = assignments.filter(hasRenderableEndpoints);
  if (relevantAssignments.length === 0) {
    return emptyScene(options.granularity ?? 'month');
  }

  const origin = snapPoint(options.origin ?? DEFAULT_ORIGIN);
  const idPrefix = options.idPrefix ?? `atlas-gantt-${Date.now().toString(36)}`;
  const asOf = toDate(options.asOf) ?? new Date();
  const range = resolveRange(relevantAssignments, asOf, options);
  const granularity = options.granularity ?? inferGranularity(range.start, range.endExclusive);
  const periods = buildPeriods(range.start, range.endExclusive, granularity);
  const people = buildPeople(relevantAssignments);
  const aggregateSpec = resolveAggregateSpec(relevantAssignments, people.length, options);
  const rows = aggregateSpec
    ? buildAggregateRows(relevantAssignments, aggregateSpec)
    : people;
  const maxPeople = boundedLimit(options.maxPeople, DEFAULT_MAX_PEOPLE);
  const maxPeriods = boundedLimit(options.maxPeriods, DEFAULT_MAX_PERIODS);
  const visiblePeople = rows.slice(0, maxPeople);
  const visiblePeriods = periods.slice(0, maxPeriods);

  if (visiblePeople.length === 0 || visiblePeriods.length === 0) {
    return emptyScene(granularity);
  }

  const visibleRange = {
    start: visiblePeriods[0]?.start ?? range.start,
    endExclusive: visiblePeriods[visiblePeriods.length - 1]?.endExclusive ?? range.endExclusive,
  };
  const cellSummaries = buildCellSummaries(visiblePeople, visiblePeriods, visibleRange);
  const tableId = diagramId(idPrefix, 'table');
  const table = makeTableNode(tableId, origin, visiblePeople, visiblePeriods, granularity, cellSummaries);
  const barNodes = makeBarNodes({
    idPrefix,
    tableId,
    tableOrigin: origin,
    people: visiblePeople,
    periods: visiblePeriods,
    range: visibleRange,
  });
  const capacityGuideNodes = makeCapacityGuideNodes({
    idPrefix,
    tableId,
    tableOrigin: origin,
    cellSummaries,
  });
  const todayMarkerNodes = makeTodayMarkerNodes({
    idPrefix,
    tableOrigin: origin,
    asOf,
    periods: visiblePeriods,
    peopleCount: visiblePeople.length,
  });
  const legendNodes = makeLegendNodes({
    idPrefix,
    tableOrigin: origin,
    tableWidth: PERSON_COLUMN_WIDTH + PERIOD_COLUMN_WIDTH * visiblePeriods.length,
    tableHeight: HEADER_HEIGHT + ROW_HEIGHT * visiblePeople.length,
  });
  const visibleAssignmentIds = new Set(visiblePeople.flatMap((person) =>
    person.assignments.map((assignment) => assignment.relationshipRecordId),
  ));
  const visiblePeriodKeys = new Set(visiblePeriods.map((period) => period.key));
  const hiddenAssignmentCount = countHiddenAssignments(
    relevantAssignments,
    visibleAssignmentIds,
    visiblePeriodKeys,
    visiblePeriods,
    visibleRange,
  );

  return {
    nodes: [table, ...capacityGuideNodes, ...barNodes, ...todayMarkerNodes, ...legendNodes],
    edges: [],
    granularity,
    periodLabels: visiblePeriods.map((period) => period.label),
    cellSummaries: cellSummaries.map(toPublicCellSummary),
    rowGrouping: aggregateSpec ? aggregateSpec.id : 'person',
    ...(aggregateSpec ? { aggregateField: aggregateSpec.id } : {}),
    scaleSummary: {
      totalPeople: aggregateSpec ? rows.length : people.length,
      visiblePeople: visiblePeople.length,
      totalPeriods: periods.length,
      visiblePeriods: visiblePeriods.length,
      totalAssignments: relevantAssignments.length,
      hiddenPeopleCount: Math.max(0, rows.length - visiblePeople.length),
      hiddenPeriodCount: Math.max(0, periods.length - visiblePeriods.length),
      hiddenAssignmentCount,
      bounded: rows.length > visiblePeople.length ||
        periods.length > visiblePeriods.length ||
        hiddenAssignmentCount > 0,
    },
    hiddenPeopleCount: Math.max(0, rows.length - visiblePeople.length),
    hiddenPeriodCount: Math.max(0, periods.length - visiblePeriods.length),
    hiddenAssignmentCount,
  };
}

function makeTableNode(
  tableId: string,
  origin: { readonly x: number; readonly y: number },
  people: readonly PersonBucket[],
  periods: readonly GanttPeriod[],
  granularity: OrgAllocationGanttGranularity,
  cellSummaries: readonly InternalCellSummary[],
): Node {
  const columnWidths = [PERSON_COLUMN_WIDTH, ...periods.map(() => PERIOD_COLUMN_WIDTH)];
  const rowHeights = people.map(() => ROW_HEIGHT);
  const width = sum(columnWidths);
  const height = HEADER_HEIGHT + sum(rowHeights);
  const cellText: Record<string, string> = {
    '0,0': people.some((person) => person.rowKind === 'aggregate') ? 'Group' : 'Person',
  };
  const cellStyles: Record<string, TableCellStyle> = {
    '0,0': headerStyle('left'),
  };
  const summariesByCell = new Map(cellSummaries.map((summary) => [summary.cellKey, summary]));

  periods.forEach((period, index) => {
    const column = index + 1;
    cellText[`0,${column}`] = period.label;
    cellStyles[`0,${column}`] = headerStyle('center');
  });

  people.forEach((person, personIndex) => {
    const row = personIndex + 1;
    cellText[`${row},0`] = person.personLabel;
    cellStyles[`${row},0`] = {
      fillColor: '#F8FAFC',
      textColor: '#0F172A',
      fontWeight: 'bold',
      fontSize: 12,
      textAlign: 'left',
      verticalAlign: 'middle',
    };

    periods.forEach((_, periodIndex) => {
      const column = periodIndex + 1;
      const summary = summariesByCell.get(`${row},${column}`);
      if (summary && summary.loadBand !== 'empty') {
        cellStyles[`${row},${column}`] = loadBandCellStyle(summary.loadBand);
      }
    });
  });

  return {
    id: tableId,
    type: 'table',
    category: 'orgchart',
    x: origin.x,
    y: origin.y,
    width,
    height,
    w: width,
    h: height,
    zIndex: 0,
    attrs: {
      schemaId: 'atlas-workload-gantt',
      rowCount: people.length,
      columnCount: periods.length + 1,
      rowHeights,
      columnWidths,
      showHeader: true,
      showHeaderColumn: true,
      headerRowIndices: [0],
      headerColIndices: [0],
      headerHeight: HEADER_HEIGHT,
      headerFillColor: '#EEF2FF',
      cellFillColor: '#FFFFFF',
      gridColor: '#CBD5E1',
      textColor: '#0F172A',
      cellText,
      cellStyles,
      atlasGantt: {
        kind: 'workload_gantt_table',
        granularity,
        peopleCount: people.length,
        periodCount: periods.length,
        rowGrouping: people[0]?.rowKind === 'aggregate'
          ? people[0].aggregateField ?? 'aggregate'
          : 'person',
        cellSummaries: cellSummaries.map(toPublicCellSummary),
      },
    },
  } as Node;
}

function toPublicCellSummary(summary: InternalCellSummary): OrgAllocationGanttCellSummary {
  return {
    cellKey: summary.cellKey,
    row: summary.row,
    column: summary.column,
    personExternalId: summary.personExternalId,
    personLabel: summary.personLabel,
    periodKey: summary.periodKey,
    periodLabel: summary.periodLabel,
    knownLoad: summary.knownLoad,
    capacityUnits: summary.capacityUnits,
    memberCount: summary.memberCount,
    unknownAllocationCount: summary.unknownAllocationCount,
    targetCount: summary.targetCount,
    assignmentCount: summary.assignmentCount,
    sourceCount: summary.sourceCount,
    loadBand: summary.loadBand,
    overage: summary.overage,
    rowKind: summary.rowKind,
    ...(summary.aggregateField ? { aggregateField: summary.aggregateField } : {}),
    ...(summary.aggregateValue ? { aggregateValue: summary.aggregateValue } : {}),
  };
}

function makeBarNodes(input: {
  readonly idPrefix: string;
  readonly tableId: string;
  readonly tableOrigin: { readonly x: number; readonly y: number };
  readonly people: readonly PersonBucket[];
  readonly periods: readonly GanttPeriod[];
  readonly range: { readonly start: Date; readonly endExclusive: Date };
}): Node[] {
  const nodes: Node[] = [];
  const columnStarts = [
    0,
    PERSON_COLUMN_WIDTH,
    ...input.periods.slice(1).map((_, index) => PERSON_COLUMN_WIDTH + PERIOD_COLUMN_WIDTH * (index + 1)),
  ];
  const cellWidth = PERIOD_COLUMN_WIDTH - BAR_X_PADDING * 2;
  const contentHeight = ROW_HEIGHT - BAR_BOTTOM_PADDING * 2;

  input.people.forEach((person, personIndex) => {
    const row = personIndex + 1;
    input.periods.forEach((period, periodIndex) => {
      const column = periodIndex + 1;
      const groups = groupsForCell(person.assignments, period, input.range);
      if (groups.length === 0) return;

      const knownLoad = sum(groups.map((group) => group.allocation));
      const gapReserve = BAR_GAP * Math.max(0, groups.length - 1);
      const barAreaHeight = Math.max(MIN_BAR_HEIGHT, contentHeight - gapReserve);
      const capacityUnits = Math.max(1, person.capacityUnits);
      const scaleMax = Math.max(capacityUnits, knownLoad);
      const scale = barAreaHeight / scaleMax;
      let yCursor = input.tableOrigin.y + HEADER_HEIGHT + personIndex * ROW_HEIGHT + ROW_HEIGHT - BAR_BOTTOM_PADDING;

      groups.forEach((group, groupIndex) => {
        const color = colorForTarget(group.targetExternalId);
        const sourceStyle = sourceStateStyle(group.sourceState);
        const height = group.hasUnknownAllocation
          ? UNKNOWN_BAR_HEIGHT
          : clamp(group.allocation * scale, MIN_BAR_HEIGHT, barAreaHeight);
        yCursor -= height;
        const x = input.tableOrigin.x + columnStarts[column] + BAR_X_PADDING;
        const y = yCursor;
        const label = [
          group.targetLabel,
          group.hasUnknownAllocation ? 'unknown' : formatAllocation(group.allocation),
          sourceStyle.labelSuffix,
        ].filter(Boolean).join(' ');
        nodes.push({
          id: diagramId(input.idPrefix, 'bar', person.personExternalId, period.key, group.targetExternalId, String(groupIndex)),
          type: 'basic-shape',
          category: 'orgchart',
          x,
          y,
          width: cellWidth,
          height,
          w: cellWidth,
          h: height,
          text: label,
          fillColor: color,
          strokeColor: sourceStyle.strokeColor ?? color,
          strokeWidth: sourceStyle.strokeWidth,
          zIndex: 10 + groupIndex,
          attrs: {
            variant: 'rectangle',
            label,
            fillColor: color,
            strokeColor: sourceStyle.strokeColor ?? color,
            strokeWidth: sourceStyle.strokeWidth,
            ...(sourceStyle.opacity < 1 ? { opacity: sourceStyle.opacity } : {}),
            ...(sourceStyle.dash ? { dash: sourceStyle.dash, dashEnabled: true } : {}),
            textColor: '#FFFFFF',
            fontSize: 10,
            fontWeight: 'bold',
            textAlign: 'left',
            verticalAlign: 'middle',
            tableDock: {
              tableId: input.tableId,
              cellKey: `${row},${column}`,
              offset: {
                x: x - (input.tableOrigin.x + columnStarts[column]),
                y: y - (input.tableOrigin.y + HEADER_HEIGHT + personIndex * ROW_HEIGHT),
              },
            },
            atlasGantt: {
              kind: 'workload_bar',
              rowKind: person.rowKind,
              personExternalId: person.personExternalId,
              personLabel: person.personLabel,
              memberCount: person.memberCount,
              capacityUnits,
              ...(person.aggregateField ? {
                aggregateField: person.aggregateField,
                aggregateValue: person.aggregateValue,
                aggregateFilterField: person.aggregateFilterField,
                aggregateFilterValue: person.aggregateFilterValue,
              } : {}),
              targetExternalId: group.targetExternalId,
              targetLabel: group.targetLabel,
              targetKind: group.targetKind,
              periodKey: period.key,
              periodLabel: period.label,
              allocation: group.allocation,
              hasUnknownAllocation: group.hasUnknownAllocation,
              role: group.role,
              assignmentCount: group.assignments.length,
              relationshipRecordIds: group.assignments.map((assignment) => assignment.relationshipRecordId),
              relationshipExternalIds: uniqueStrings(group.assignments.map((assignment) => assignment.relationshipExternalId)),
              validFrom: firstNonEmpty(group.assignments.map((assignment) => assignment.validFrom)),
              validTo: firstNonEmpty(group.assignments.map((assignment) => assignment.validTo)),
              sourceState: group.sourceState,
              confidence: group.confidence,
              sourceLabels: group.sourceLabels,
            },
          },
        } as Node);
        yCursor -= BAR_GAP;
      });
    });
  });

  return nodes;
}

function buildCellSummaries(
  people: readonly PersonBucket[],
  periods: readonly GanttPeriod[],
  range: { readonly start: Date; readonly endExclusive: Date },
): InternalCellSummary[] {
  const summaries: InternalCellSummary[] = [];
  people.forEach((person, personIndex) => {
    const row = personIndex + 1;
    periods.forEach((period, periodIndex) => {
      const column = periodIndex + 1;
      const groups = groupsForCell(person.assignments, period, range);
      const knownLoad = sum(groups.map((group) => group.allocation));
      const unknownAllocationCount = groups.reduce(
        (total, group) => total + (group.hasUnknownAllocation ? group.assignments.length : 0),
        0,
      );
      const sourceLabels = uniqueStrings(groups.flatMap((group) => group.sourceLabels));
      const capacityUnits = Math.max(1, person.capacityUnits);
      summaries.push({
        cellKey: `${row},${column}`,
        row,
        column,
        personExternalId: person.personExternalId,
        personLabel: person.personLabel,
        periodKey: period.key,
        periodLabel: period.label,
        knownLoad,
        capacityUnits,
        memberCount: person.memberCount,
        unknownAllocationCount,
        targetCount: groups.length,
        assignmentCount: groups.reduce((total, group) => total + group.assignments.length, 0),
        sourceCount: sourceLabels.length,
        loadBand: loadBand(knownLoad / capacityUnits, unknownAllocationCount, groups.length),
        overage: Math.max(0, knownLoad - capacityUnits),
        rowKind: person.rowKind,
        aggregateField: person.aggregateField,
        aggregateValue: person.aggregateValue,
        groups,
      });
    });
  });
  return summaries;
}

function makeCapacityGuideNodes(input: {
  readonly idPrefix: string;
  readonly tableId: string;
  readonly tableOrigin: { readonly x: number; readonly y: number };
  readonly cellSummaries: readonly InternalCellSummary[];
}): Node[] {
  return input.cellSummaries
    .filter((summary) => summary.targetCount > 0 || summary.unknownAllocationCount > 0)
    .map((summary) => {
      const cellLeft = input.tableOrigin.x + PERSON_COLUMN_WIDTH + (summary.column - 1) * PERIOD_COLUMN_WIDTH;
      const cellTop = input.tableOrigin.y + HEADER_HEIGHT + (summary.row - 1) * ROW_HEIGHT;
      const contentHeight = ROW_HEIGHT - BAR_BOTTOM_PADDING * 2;
      const barAreaHeight = Math.max(MIN_BAR_HEIGHT, contentHeight - BAR_GAP * Math.max(0, summary.targetCount - 1));
      const capacityUnits = Math.max(1, summary.capacityUnits);
      const scaleMax = Math.max(capacityUnits, summary.knownLoad);
      const scale = barAreaHeight / scaleMax;
      const guideY = cellTop + ROW_HEIGHT - BAR_BOTTOM_PADDING - Math.max(MIN_BAR_HEIGHT, capacityUnits * scale);
      const x = cellLeft + BAR_X_PADDING;
      const y = clamp(guideY, cellTop + BAR_BOTTOM_PADDING, cellTop + ROW_HEIGHT - BAR_BOTTOM_PADDING);
      return {
        id: diagramId(input.idPrefix, 'capacity-guide', summary.personExternalId, summary.periodKey),
        type: 'basic-shape',
        category: 'orgchart',
        x,
        y,
        width: PERIOD_COLUMN_WIDTH - BAR_X_PADDING * 2,
        height: CAPACITY_GUIDE_HEIGHT,
        w: PERIOD_COLUMN_WIDTH - BAR_X_PADDING * 2,
        h: CAPACITY_GUIDE_HEIGHT,
        fillColor: '#0F172A',
        strokeColor: '#0F172A',
        strokeWidth: 0,
        zIndex: 24,
        attrs: {
          variant: 'rectangle',
          fillColor: '#0F172A',
          strokeColor: '#0F172A',
          strokeWidth: 0,
          opacity: summary.knownLoad > 1 ? 0.45 : 0.2,
          tableDock: {
            tableId: input.tableId,
            cellKey: summary.cellKey,
            offset: {
              x: x - cellLeft,
              y: y - cellTop,
            },
          },
          atlasGantt: {
            kind: 'capacity_guide',
            personExternalId: summary.personExternalId,
            periodKey: summary.periodKey,
            knownLoad: summary.knownLoad,
            capacityUnits,
            memberCount: summary.memberCount,
          },
        },
      } as Node;
    });
}

function makeTodayMarkerNodes(input: {
  readonly idPrefix: string;
  readonly tableOrigin: { readonly x: number; readonly y: number };
  readonly asOf: Date;
  readonly periods: readonly GanttPeriod[];
  readonly peopleCount: number;
}): Node[] {
  const period = input.periods.find((candidate) =>
    input.asOf.getTime() >= candidate.start.getTime() &&
    input.asOf.getTime() < candidate.endExclusive.getTime(),
  );
  if (!period) return [];
  const periodIndex = input.periods.indexOf(period);
  const duration = period.endExclusive.getTime() - period.start.getTime();
  const progress = duration > 0
    ? clamp((input.asOf.getTime() - period.start.getTime()) / duration, 0, 1)
    : 0;
  const x = input.tableOrigin.x + PERSON_COLUMN_WIDTH + periodIndex * PERIOD_COLUMN_WIDTH + progress * PERIOD_COLUMN_WIDTH;
  const markerHeight = HEADER_HEIGHT + input.peopleCount * ROW_HEIGHT;
  const markerId = diagramId(input.idPrefix, 'today-marker', period.key);
  return [
    {
      id: markerId,
      type: 'basic-shape',
      category: 'orgchart',
      x: x - 1.5,
      y: input.tableOrigin.y,
      width: 3,
      height: markerHeight,
      w: 3,
      h: markerHeight,
      fillColor: '#E11D48',
      strokeColor: '#E11D48',
      strokeWidth: 0,
      zIndex: 32,
      attrs: {
        variant: 'rectangle',
        fillColor: '#E11D48',
        strokeColor: '#E11D48',
        strokeWidth: 0,
        opacity: 0.72,
        atlasGantt: {
          kind: 'today_marker',
          periodKey: period.key,
          asOf: input.asOf.toISOString(),
        },
      },
    } as Node,
    {
      id: diagramId(input.idPrefix, 'today-label', period.key),
      type: 'basic-shape',
      category: 'orgchart',
      x: x - 34,
      y: input.tableOrigin.y - 30,
      width: 68,
      height: 22,
      w: 68,
      h: 22,
      text: 'As of',
      fillColor: '#FFF1F2',
      strokeColor: '#E11D48',
      strokeWidth: 1,
      zIndex: 33,
      attrs: {
        variant: 'rectangle',
        label: 'As of',
        fillColor: '#FFF1F2',
        strokeColor: '#E11D48',
        strokeWidth: 1,
        textColor: '#9F1239',
        fontSize: 10,
        fontWeight: 'bold',
        atlasGantt: {
          kind: 'today_label',
          periodKey: period.key,
          asOf: input.asOf.toISOString(),
        },
      },
    } as Node,
  ];
}

function makeLegendNodes(input: {
  readonly idPrefix: string;
  readonly tableOrigin: { readonly x: number; readonly y: number };
  readonly tableWidth: number;
  readonly tableHeight: number;
}): Node[] {
  const x = input.tableOrigin.x;
  const y = input.tableOrigin.y + input.tableHeight + 24;
  const items: Array<{ readonly id: string; readonly label: string; readonly fill: string; readonly stroke?: string; readonly text?: string }> = [
    { id: 'under', label: '<85%', fill: '#F0FDF4', stroke: '#BBF7D0', text: '#166534' },
    { id: 'near', label: '85-100%', fill: '#FFF7ED', stroke: '#FED7AA', text: '#9A3412' },
    { id: 'over', label: '>100%', fill: '#FEF2F2', stroke: '#FCA5A5', text: '#991B1B' },
    { id: 'unknown', label: 'Unknown', fill: '#FFFBEB', stroke: '#FCD34D', text: '#92400E' },
    { id: 'guide', label: 'Guide', fill: '#E2E8F0', stroke: '#0F172A', text: '#0F172A' },
  ];
  const nodes: Node[] = [
    {
      id: diagramId(input.idPrefix, 'legend-title'),
      type: 'basic-shape',
      category: 'orgchart',
      x,
      y,
      width: 112,
      height: LEGEND_ITEM_HEIGHT,
      w: 112,
      h: LEGEND_ITEM_HEIGHT,
      text: 'Load legend',
      fillColor: '#FFFFFF',
      strokeColor: '#CBD5E1',
      strokeWidth: 1,
      zIndex: 40,
      attrs: {
        variant: 'rectangle',
        label: 'Load legend',
        fillColor: '#FFFFFF',
        strokeColor: '#CBD5E1',
        strokeWidth: 1,
        textColor: '#334155',
        fontSize: 10,
        fontWeight: 'bold',
        atlasGantt: { kind: 'legend_title' },
      },
    } as Node,
  ];
  items.forEach((item, index) => {
    const itemX = x + 120 + index * (LEGEND_ITEM_WIDTH + LEGEND_GAP);
    if (itemX + LEGEND_ITEM_WIDTH > x + input.tableWidth) return;
    nodes.push({
      id: diagramId(input.idPrefix, 'legend-item', item.id),
      type: 'basic-shape',
      category: 'orgchart',
      x: itemX,
      y,
      width: LEGEND_ITEM_WIDTH,
      height: LEGEND_ITEM_HEIGHT,
      w: LEGEND_ITEM_WIDTH,
      h: LEGEND_ITEM_HEIGHT,
      text: item.label,
      fillColor: item.fill,
      strokeColor: item.stroke ?? item.fill,
      strokeWidth: 1,
      zIndex: 40 + index,
      attrs: {
        variant: 'rectangle',
        label: item.label,
        fillColor: item.fill,
        strokeColor: item.stroke ?? item.fill,
        strokeWidth: 1,
        textColor: item.text ?? '#0F172A',
        fontSize: 10,
        fontWeight: 'bold',
        atlasGantt: {
          kind: 'legend_item',
          legendId: item.id,
        },
      },
    } as Node);
  });
  return nodes;
}

function groupsForCell(
  assignments: readonly OrgAllocationAssignment[],
  period: GanttPeriod,
  range: { readonly start: Date; readonly endExclusive: Date },
): BarGroup[] {
  const groups = new Map<string, OrgAllocationAssignment[]>();
  for (const assignment of assignments) {
    if (!overlapsPeriod(assignment, period, range)) continue;
    const bucket = groups.get(assignment.targetExternalId) ?? [];
    bucket.push(assignment);
    groups.set(assignment.targetExternalId, bucket);
  }

  return Array.from(groups.entries())
    .map(([targetExternalId, rows]) => {
      const confidences = rows
        .map((row) => row.confidence)
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
      return {
        targetExternalId,
        targetLabel: rows[0]?.targetLabel ?? targetExternalId,
        targetKind: rows[0]?.targetKind ?? 'work',
        role: firstNonEmpty(rows.map((row) => row.role)),
        allocation: sum(rows.map((row) => row.allocation ?? 0)),
        hasUnknownAllocation: rows.some((row) => row.hasUnspecifiedAllocation),
        sourceState: sourceStateForAssignments(rows),
        confidence: confidences.length > 0 ? Math.min(...confidences) : null,
        sourceLabels: uniqueStrings(rows.map(sourceLabelForAssignment)),
        assignments: rows,
      };
    })
    .sort((left, right) => {
      const byAllocation = right.allocation - left.allocation;
      if (Math.abs(byAllocation) > 0.0001) return byAllocation;
      return left.targetLabel.localeCompare(right.targetLabel);
    });
}

function countHiddenAssignments(
  assignments: readonly OrgAllocationAssignment[],
  visibleAssignmentIds: ReadonlySet<string>,
  visiblePeriodKeys: ReadonlySet<string>,
  visiblePeriods: readonly GanttPeriod[],
  range: { readonly start: Date; readonly endExclusive: Date },
): number {
  if (visiblePeriods.length === 0) return assignments.length;
  let count = 0;
  for (const assignment of assignments) {
    if (!visibleAssignmentIds.has(assignment.relationshipRecordId)) {
      count += 1;
      continue;
    }
    const hasVisiblePeriod = visiblePeriods.some((period) =>
      visiblePeriodKeys.has(period.key) && overlapsPeriod(assignment, period, range),
    );
    if (!hasVisiblePeriod) count += 1;
  }
  return count;
}

function buildPeople(assignments: readonly OrgAllocationAssignment[]): PersonBucket[] {
  return Array.from(groupBy(assignments, (assignment) => assignment.personExternalId))
    .map(([personExternalId, rows]) => ({
      personExternalId,
      personLabel: rows[0]?.personLabel ?? personExternalId,
      rowKind: 'person' as const,
      memberCount: 1,
      capacityUnits: 1,
      assignments: rows,
      totalKnownAllocation: sum(rows.map((row) => row.allocation ?? 0)),
      assignmentCount: rows.length,
    }))
    .sort((left, right) => {
      const byAllocation = right.totalKnownAllocation - left.totalKnownAllocation;
      if (Math.abs(byAllocation) > 0.0001) return byAllocation;
      const byCount = right.assignmentCount - left.assignmentCount;
      if (byCount !== 0) return byCount;
      return left.personLabel.localeCompare(right.personLabel);
    });
}

function resolveAggregateSpec(
  assignments: readonly OrgAllocationAssignment[],
  personCount: number,
  options: OrgAllocationGanttSceneOptions,
): AggregateSpec | null {
  const grouping = options.rowGrouping ?? 'auto';
  if (grouping === 'person') return null;
  const threshold = boundedLimit(options.aggregateThreshold, DEFAULT_AGGREGATE_THRESHOLD);
  if (grouping === 'auto' && personCount <= threshold) return null;

  const candidates = grouping === 'auto'
    ? AGGREGATE_SPECS
    : AGGREGATE_SPECS.filter((spec) => spec.id === grouping);

  for (const spec of candidates) {
    const values = new Set(assignments
      .map((assignment) => aggregateValueForAssignment(assignment, spec).value)
      .filter((value) => value !== 'Unassigned'));
    if (values.size >= 1) return spec;
  }

  return null;
}

function buildAggregateRows(
  assignments: readonly OrgAllocationAssignment[],
  spec: AggregateSpec,
): PersonBucket[] {
  const rowsByKey = groupBy(assignments, (assignment) => aggregateValueForAssignment(assignment, spec).key);
  return Array.from(rowsByKey.entries())
    .map(([key, rows]) => {
      const first = rows[0];
      const value = first ? aggregateValueForAssignment(first, spec) : {
        key,
        label: key,
        value: key,
        filterField: spec.filterField,
        filterValue: key,
      };
      const people = new Set(rows.map((assignment) => assignment.personExternalId));
      const memberCount = Math.max(1, people.size);
      const label = `${spec.label}: ${value.label} (${memberCount} ${memberCount === 1 ? 'person' : 'people'})`;
      return {
        personExternalId: `aggregate:${spec.id}:${safeToken(value.key)}`,
        personLabel: label,
        rowKind: 'aggregate' as const,
        aggregateField: spec.id,
        aggregateValue: value.value,
        aggregateFilterField: value.filterField,
        aggregateFilterValue: value.filterValue,
        memberCount,
        capacityUnits: memberCount,
        assignments: rows,
        totalKnownAllocation: sum(rows.map((row) => row.allocation ?? 0)),
        assignmentCount: rows.length,
      };
    })
    .sort((left, right) => {
      const byAllocation = right.totalKnownAllocation - left.totalKnownAllocation;
      if (Math.abs(byAllocation) > 0.0001) return byAllocation;
      const byMembers = right.memberCount - left.memberCount;
      if (byMembers !== 0) return byMembers;
      return left.personLabel.localeCompare(right.personLabel);
    });
}

function aggregateValueForAssignment(
  assignment: OrgAllocationAssignment,
  spec: AggregateSpec,
): {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly filterField: string;
  readonly filterValue: string;
} {
  if (spec.id === 'target') {
    return {
      key: assignment.targetExternalId,
      label: assignment.targetLabel,
      value: assignment.targetExternalId,
      filterField: spec.filterField,
      filterValue: assignment.targetExternalId,
    };
  }

  const value = firstSourceRowValue(assignment.source, spec.sourceFields) ?? 'Unassigned';
  return {
    key: value,
    label: value,
    value,
    filterField: spec.filterField,
    filterValue: value,
  };
}

function overlapsPeriod(
  assignment: OrgAllocationAssignment,
  period: GanttPeriod,
  range: { readonly start: Date; readonly endExclusive: Date },
): boolean {
  const start = toDate(assignment.validFrom) ?? range.start;
  const endExclusive = toExclusiveEndDate(assignment.validTo) ?? range.endExclusive;
  return start.getTime() < period.endExclusive.getTime() &&
    endExclusive.getTime() > period.start.getTime();
}

function resolveRange(
  assignments: readonly OrgAllocationAssignment[],
  asOf: Date,
  options: OrgAllocationGanttSceneOptions,
): { readonly start: Date; readonly endExclusive: Date } {
  const explicitStart = toDate(options.windowStart);
  const explicitEnd = toExclusiveEndDateFromInput(options.windowEnd);
  if (explicitStart && explicitEnd && explicitEnd.getTime() > explicitStart.getTime()) {
    return { start: startOfUtcDay(explicitStart), endExclusive: explicitEnd };
  }
  if (explicitStart) {
    const inferred = inferRange(assignments, asOf);
    return {
      start: startOfUtcDay(explicitStart),
      endExclusive: inferred.endExclusive.getTime() > explicitStart.getTime()
        ? inferred.endExclusive
        : addMonths(startOfMonthUtc(explicitStart), MAX_OPEN_ENDED_MONTHS),
    };
  }
  if (explicitEnd) {
    const inferred = inferRange(assignments, asOf);
    return {
      start: inferred.start.getTime() < explicitEnd.getTime()
        ? inferred.start
        : startOfMonthUtc(addMonths(explicitEnd, -MAX_OPEN_ENDED_MONTHS)),
      endExclusive: explicitEnd,
    };
  }
  return inferRange(assignments, asOf);
}

function inferRange(
  assignments: readonly OrgAllocationAssignment[],
  asOf: Date,
): { readonly start: Date; readonly endExclusive: Date } {
  const starts = assignments
    .map((assignment) => toDate(assignment.validFrom))
    .filter((date): date is Date => !!date);
  const explicitEnds = assignments
    .map((assignment) => toExclusiveEndDate(assignment.validTo))
    .filter((date): date is Date => !!date);
  const openEndedStarts = assignments
    .filter((assignment) => !assignment.validTo)
    .map((assignment) => toDate(assignment.validFrom) ?? asOf);

  const activeOpenEndedAtAsOf = assignments.some((assignment) => {
    if (assignment.validTo) return false;
    const start = toDate(assignment.validFrom);
    return !start || start.getTime() <= asOf.getTime();
  });

  const fallbackStart = startOfMonthUtc(asOf);
  let start = minDate(starts) ?? fallbackStart;
  if (activeOpenEndedAtAsOf && asOf.getTime() < start.getTime()) {
    start = startOfMonthUtc(asOf);
  }

  const openEndedMax = maxDate(openEndedStarts);
  const openEndedFallbackEnd = openEndedMax
    ? addMonths(startOfMonthUtc(openEndedMax), MAX_OPEN_ENDED_MONTHS)
    : null;
  let endExclusive = maxDate([
    ...explicitEnds,
    ...(openEndedFallbackEnd ? [openEndedFallbackEnd] : []),
  ]) ?? addMonths(startOfMonthUtc(start), MAX_OPEN_ENDED_MONTHS);

  if (endExclusive.getTime() <= start.getTime()) {
    endExclusive = addMonths(startOfMonthUtc(start), 1);
  }

  return { start, endExclusive };
}

function inferGranularity(start: Date, endExclusive: Date): OrgAllocationGanttGranularity {
  const days = Math.ceil((endExclusive.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  return days <= 120 ? 'week' : 'month';
}

function buildPeriods(
  start: Date,
  endExclusive: Date,
  granularity: OrgAllocationGanttGranularity,
): GanttPeriod[] {
  const periods: GanttPeriod[] = [];
  let cursor = granularity === 'week'
    ? startOfWeekUtc(start)
    : granularity === 'quarter'
      ? startOfQuarterUtc(start)
      : startOfMonthUtc(start);
  const hardLimit = 260;
  while (cursor.getTime() < endExclusive.getTime() && periods.length < hardLimit) {
    const next = granularity === 'week'
      ? addDays(cursor, 7)
      : granularity === 'quarter'
        ? addMonths(cursor, 3)
        : addMonths(cursor, 1);
    periods.push({
      key: periodKey(cursor, granularity),
      label: periodLabel(cursor, granularity),
      start: cursor,
      endExclusive: next,
    });
    cursor = next;
  }
  return periods;
}

function headerStyle(textAlign: 'left' | 'center'): TableCellStyle {
  return {
    fillColor: '#EEF2FF',
    textColor: '#1E1B4B',
    fontWeight: 'bold',
    fontSize: 12,
    textAlign,
    verticalAlign: 'middle',
  };
}

function loadBandCellStyle(loadBandValue: OrgAllocationGanttLoadBand): TableCellStyle {
  switch (loadBandValue) {
    case 'severe_over':
      return {
        fillColor: '#FEE2E2',
        borderColor: '#EF4444',
        borderWidth: 1.5,
      };
    case 'over':
      return {
        fillColor: '#FEF2F2',
        borderColor: '#F87171',
        borderWidth: 1,
      };
    case 'at_capacity':
      return {
        fillColor: '#EEF2FF',
        borderColor: '#818CF8',
        borderWidth: 1,
      };
    case 'near_capacity':
      return {
        fillColor: '#FFF7ED',
        borderColor: '#FED7AA',
        borderWidth: 1,
      };
    case 'unknown':
      return {
        fillColor: '#FFFBEB',
        borderColor: '#FCD34D',
        borderWidth: 1,
        borderDash: [4, 3],
      };
    case 'under':
      return {
        fillColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        borderWidth: 1,
      };
    case 'empty':
    default:
      return {};
  }
}

function loadBand(
  knownLoad: number,
  unknownAllocationCount: number,
  targetCount: number,
): OrgAllocationGanttLoadBand {
  if (targetCount === 0) return 'empty';
  if (unknownAllocationCount > 0 && knownLoad <= 0.0001) return 'unknown';
  if (knownLoad > 1.2) return 'severe_over';
  if (knownLoad > 1.0001) return 'over';
  if (Math.abs(knownLoad - 1) <= 0.0001) return 'at_capacity';
  if (knownLoad >= 0.85) return 'near_capacity';
  return 'under';
}

function sourceStateStyle(sourceState: OrgAllocationGanttSourceState): {
  readonly labelSuffix: string;
  readonly strokeColor?: string;
  readonly strokeWidth: number;
  readonly dash?: readonly number[];
  readonly opacity: number;
} {
  switch (sourceState) {
    case 'draft':
      return { labelSuffix: 'draft', strokeColor: '#2563EB', strokeWidth: 2, dash: [5, 4], opacity: 0.76 };
    case 'needs_review':
      return { labelSuffix: 'review', strokeColor: '#F59E0B', strokeWidth: 2, dash: [3, 3], opacity: 0.9 };
    case 'stale':
      return { labelSuffix: 'stale', strokeColor: '#64748B', strokeWidth: 2, dash: [5, 3], opacity: 0.72 };
    case 'retired':
      return { labelSuffix: 'retired', strokeColor: '#475569', strokeWidth: 2, dash: [2, 3], opacity: 0.48 };
    case 'unresolved':
      return { labelSuffix: 'unresolved', strokeColor: '#D97706', strokeWidth: 2, dash: [2, 2], opacity: 0.82 };
    case 'low_confidence':
      return { labelSuffix: 'low conf.', strokeColor: '#A855F7', strokeWidth: 2, dash: [4, 3], opacity: 0.86 };
    case 'source_backed':
    default:
      return { labelSuffix: '', strokeWidth: 1, opacity: 1 };
  }
}

function sourceStateForAssignments(
  assignments: readonly OrgAllocationAssignment[],
): OrgAllocationGanttSourceState {
  if (assignments.some((assignment) =>
    assignment.basis?.toLowerCase() === 'scenario' ||
    assignment.source.scenarioOverlay === true,
  )) {
    return 'draft';
  }
  if (assignments.some((assignment) => hasState(assignment, ['retired', 'tombstoned', 'deleted', 'inactive']))) {
    return 'retired';
  }
  if (assignments.some((assignment) => hasState(assignment, ['unresolved', 'resolution_issue', 'mapping_issue']))) {
    return 'unresolved';
  }
  if (assignments.some((assignment) => hasState(assignment, ['needs_review', 'awaiting_review', 'review']))) {
    return 'needs_review';
  }
  if (assignments.some((assignment) => hasState(assignment, ['draft', 'proposed', 'scenario']))) {
    return 'draft';
  }
  if (assignments.some((assignment) => hasState(assignment, ['stale', 'expired']))) {
    return 'stale';
  }
  if (assignments.some((assignment) =>
    typeof assignment.confidence === 'number' &&
    Number.isFinite(assignment.confidence) &&
    assignment.confidence < 0.7,
  )) {
    return 'low_confidence';
  }
  return 'source_backed';
}

function hasState(
  assignment: OrgAllocationAssignment,
  states: readonly string[],
): boolean {
  const stateValues = [
    assignment.lifecycleState,
    assignment.recordStatus,
    assignment.sourceStatus,
    assignment.sourceBindingStatus,
    stringValue(assignment.source.status),
    stringValue(assignment.source.state),
    stringValue(assignment.source.lifecycleState),
  ].map((value) => value?.toLowerCase().replace(/[\s-]+/g, '_'));
  return stateValues.some((value) => !!value && states.includes(value));
}

function sourceLabelForAssignment(assignment: OrgAllocationAssignment): string {
  const label = stringValue(assignment.source.label) ??
    stringValue(assignment.source.name) ??
    stringValue(assignment.source.fileName) ??
    stringValue(assignment.source.sourceSystem);
  return label ?? 'source';
}

function colorForTarget(targetExternalId: string): string {
  const hash = [...targetExternalId].reduce((value, char) =>
    (value * 31 + char.charCodeAt(0)) % 2147483647, 0);
  return TARGET_PALETTE[Math.abs(hash) % TARGET_PALETTE.length] ?? TARGET_PALETTE[0];
}

function formatAllocation(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function periodKey(date: Date, granularity: OrgAllocationGanttGranularity): string {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  if (granularity === 'quarter') return `${year}-Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
  if (granularity === 'month') return `${year}-${month}`;
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function periodLabel(date: Date, granularity: OrgAllocationGanttGranularity): string {
  if (granularity === 'quarter') {
    return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
  }
  if (granularity === 'month') {
    return `${MONTH_LABELS[date.getUTCMonth()] ?? 'Jan'} ${date.getUTCFullYear()}`;
  }
  return `${date.getUTCDate()} ${MONTH_LABELS[date.getUTCMonth()] ?? 'Jan'}`;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toExclusiveEndDate(value: string | null): Date | null {
  const date = toDate(value);
  if (!date) return null;
  return addDays(startOfUtcDay(date), 1);
}

function toExclusiveEndDateFromInput(value: Date | string | null | undefined): Date | null {
  const date = toDate(value);
  if (!date) return null;
  return addDays(startOfUtcDay(date), 1);
}

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function startOfWeekUtc(date: Date): Date {
  const out = startOfUtcDay(date);
  const day = out.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return addDays(out, mondayOffset);
}

function startOfMonthUtc(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function startOfQuarterUtc(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1));
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function addMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

function minDate(values: readonly Date[]): Date | null {
  if (values.length === 0) return null;
  return values.reduce((min, value) => value.getTime() < min.getTime() ? value : min);
}

function maxDate(values: readonly Date[]): Date | null {
  if (values.length === 0) return null;
  return values.reduce((max, value) => value.getTime() > max.getTime() ? value : max);
}

function hasRenderableEndpoints(assignment: OrgAllocationAssignment): boolean {
  return !!assignment.personExternalId && !!assignment.targetExternalId;
}

function boundedLimit(value: number | undefined, fallback: number): number {
  if (!value || !Number.isFinite(value)) return fallback;
  return Math.max(1, Math.floor(value));
}

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function firstNonEmpty(values: readonly (string | null)[]): string | null {
  return values.find((value): value is string => !!value) ?? null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function firstSourceRowValue(source: Record<string, unknown>, fields: readonly string[]): string | null {
  const rowSnapshot = isRecord(source.rowSnapshot) ? source.rowSnapshot : null;
  if (!rowSnapshot) return null;
  for (const field of fields) {
    const normalized = normalizeFieldName(field);
    for (const [key, value] of Object.entries(rowSnapshot)) {
      if (normalizeFieldName(key) !== normalized) continue;
      const text = stringValue(value);
      if (text) return text;
    }
  }
  return null;
}

function normalizeFieldName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function uniqueStrings(values: readonly (string | null | undefined)[]): string[] {
  return Array.from(new Set(values
    .map((value) => value?.trim())
    .filter((value): value is string => !!value)));
}

function groupBy<T>(
  rows: readonly T[],
  keyFor: (row: T) => string,
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const key = keyFor(row);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }
  return groups;
}

function snapPoint(point: { readonly x: number; readonly y: number }): { readonly x: number; readonly y: number } {
  return {
    x: Math.round(point.x / 12) * 12,
    y: Math.round(point.y / 12) * 12,
  };
}

function diagramId(prefix: string, ...parts: readonly string[]): string {
  return [prefix, ...parts].map((part) =>
    part.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase(),
  ).filter(Boolean).join(':');
}

function safeToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'unassigned';
}

function emptyScene(granularity: OrgAllocationGanttGranularity): OrgAllocationGanttScene {
  return {
    nodes: [],
    edges: [],
    granularity,
    periodLabels: [],
    cellSummaries: [],
    rowGrouping: 'person',
    scaleSummary: {
      totalPeople: 0,
      visiblePeople: 0,
      totalPeriods: 0,
      visiblePeriods: 0,
      totalAssignments: 0,
      hiddenPeopleCount: 0,
      hiddenPeriodCount: 0,
      hiddenAssignmentCount: 0,
      bounded: false,
    },
    hiddenPeopleCount: 0,
    hiddenPeriodCount: 0,
    hiddenAssignmentCount: 0,
  };
}
